import { writable, get } from "svelte/store";
import { deviceHeaders, getDeviceId, getDeviceInfo } from "./device-id";

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL ?? "https://localhost:4000";

const AUTH_TOKEN_KEY = "excel-agent-token";
const AUTH_USER_KEY = "excel-agent-user";

export interface AuthUser {
    id: number;
    username: string;
    role?: "ADMIN" | "USER" | "OPS";
    isAdmin: boolean;
    deviceId?: string;
}

export interface AuthState {
    token: string | null;
    user: AuthUser | null;
}

function localizeBackendMessage(message: string, status: number): string {
    if (/license has expired/i.test(message)) return "License của bạn đã hết hạn";
    if (/license is not active yet/i.test(message)) return "License của bạn chưa có hiệu lực";
    if (status === 503 && /maintenance/i.test(message)) return "Hệ thống đang bảo trì";
    return message;
}

async function getErrorMessage(res: Response, fallback: string): Promise<string> {
    const err = await res.json().catch(() => ({})) as { message?: unknown; detail?: unknown };
    const raw = err.message ?? err.detail;
    const message = typeof raw === "string" ? raw : fallback;
    return localizeBackendMessage(message, res.status);
}

function createAuthStore() {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    const userRaw = localStorage.getItem(AUTH_USER_KEY);
    let user: AuthUser | null = null;
    try { user = userRaw ? JSON.parse(userRaw) : null; } catch { user = null; }

    const store = writable<AuthState>({ token, user });

    // Validate stored token against current backend on startup.
    // If the backend changed (e.g. dev vs prod), the old JWT is invalid.
    if (token) {
        fetch(`${BACKEND_URL}/chat/llm-config`, {
            headers: { Authorization: `Bearer ${token}`, ...deviceHeaders() },
        }).then(res => {
            if (res.status === 401) {
                console.warn('[Auth] Stored token is invalid (401), clearing session');
                localStorage.removeItem(AUTH_TOKEN_KEY);
                localStorage.removeItem(AUTH_USER_KEY);
                store.set({ token: null, user: null });
            }
        }).catch(() => {
            // Network error — keep token, user might be offline
        });
    }

    return {
        subscribe: store.subscribe,

        get snapshot(): AuthState {
            return get(store);
        },

        async login(username: string, password: string): Promise<void> {
            const res = await fetch(`${BACKEND_URL}/auth/login`, {
                method: "POST",
                headers: { "Content-Type": "application/json", ...deviceHeaders() },
                body: JSON.stringify({ username, password, deviceId: getDeviceId(), deviceInfo: getDeviceInfo() }),
            });
            if (!res.ok) {
                if (res.status === 400) {
                    throw new Error("Có lỗi sảy ra, vui lòng thử lại");
                }
                throw new Error(await getErrorMessage(res, "Login failed"));
            }
            const data = await res.json() as { access_token: string; user: AuthUser };
            localStorage.setItem(AUTH_TOKEN_KEY, data.access_token);
            localStorage.setItem(AUTH_USER_KEY, JSON.stringify(data.user));
            store.set({ token: data.access_token, user: data.user });
        },

        logout(): void {
            localStorage.removeItem(AUTH_TOKEN_KEY);
            localStorage.removeItem(AUTH_USER_KEY);
            store.set({ token: null, user: null });
        },
    };
}

export const authStore = createAuthStore();

export function getAuthToken(): string | null {
    return authStore.snapshot.token;
}
